import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useState } from 'react'
import moment from 'moment'
import styles from './styles.module.css'
import { useSchedulingData } from './hooks/useSchedulingData'
import CalendarView from './components/CalendarView'
import TechnicianPanel from './components/TechnicianPanel'
import DaySettingsEditor from './components/DaySettingsEditor'
import ImpactPreviewModal from './components/ImpactPreviewModal'
import { getActiveSchedulePattern } from '../../../../../global/utils/schedule-patterns'

interface DaySettingsManagementModalProps {
  isOpen: boolean
  onClose: () => void
  locationObjectId: string
  technicianObjectId?: string // Optional - focuses on single technician
}

export default function DaySettingsManagementModal({
  isOpen,
  onClose,
  locationObjectId,
  technicianObjectId,
}: DaySettingsManagementModalProps) {
  // Calendar navigation state
  const [calendarDate, setCalendarDate] = useState(() => {
    // Default to first day of current month for consistent calendar view
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), 1)
  })

  // Day editor state
  const [dayEditorOpen, setDayEditorOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTechnicianId, setSelectedTechnicianId] = useState<
    string | null
  >(null)

  // Use the custom scheduling data hook
  const {
    location,
    techniciansWithScheduling,
    loading,
    technicianStatuses,
    visibleTechnicianIds,
    expandedScheduleEditors,
    editingSchedules,
    draftSchedulePatterns,
    impactModalOpen,
    impactModalData,
    toggleTechnicianVisibility,
    toggleScheduleEditor,
    startEditingSchedule,
    cancelEditingSchedule,
    updateEditingData,
    toggleDayInDraft,
    addWeekToDraft,
    removeWeekFromDraft,
    handleCheckChanges,
    fetchSchedulingData,
    handleImpactModalClose,
    handleConfirmOriginalDate,
    handleUseSafeDate,
    checkingChanges,
    savingPattern,
  } = useSchedulingData({
    isOpen,
    locationObjectId,
    technicianObjectId,
    calendarDate,
  })

  // Day click handler (general day click)
  const handleDayClick = (date: Date) => {
    console.log('Day clicked:', moment(date).format('YYYY-MM-DD'))
    setSelectedDate(date)
    setSelectedTechnicianId(null) // Clear specific technician selection
    setDayEditorOpen(true)
  }

  // Technician day click handler (specific technician click)
  const handleTechnicianDayClick = (date: Date, technicianId: string) => {
    console.log(
      'Technician day clicked:',
      moment(date).format('YYYY-MM-DD'),
      technicianId,
    )
    setSelectedDate(date)
    setSelectedTechnicianId(technicianId)
    setDayEditorOpen(true)
  }

  // Get specific technician data for date (for day editor)
  const getTechnicianDataForDate = (date: Date, technicianId: string) => {
    const dateKey = moment(date).format('YYYY-MM-DD')
    const dayStatuses = technicianStatuses.get(dateKey) || []

    const status = dayStatuses.find(
      (s) => s.technicianObjectId === technicianId,
    )
    if (!status) return null

    const techData = techniciansWithScheduling.find(
      (t) => t.objectId === status.technicianObjectId,
    )
    if (!techData) return null

    const daySetting = techData.daySettings.find(
      (ds) => moment.utc(ds.day).format('YYYY-MM-DD') === dateKey,
    )

    return {
      technician: techData, // Full ITechnician object
      currentDaySetting: daySetting || null,
      isWorkingDay: status.status === 'working',
      isHumanTouched: status.isHumanTouched,
      isHoliday: status.status === 'holiday',
      holidayName: status.holidayName,
    }
  }

  // Save day changes handler
  const handleSaveDayChanges = async (
    technicianId: string,
    daySettingData: any,
  ) => {
    console.log('Saving day changes:', technicianId, daySettingData)
    // TODO: Implement API call to save individual day changes
    setDayEditorOpen(false)
    await fetchSchedulingData() // Refresh data
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        className: styles.modalPaper,
      }}
    >
      <DialogTitle className={styles.modalHeader}>
        <div className={styles.headerContent}>
          <h2 className={styles.modalTitle}>
            Manage Technician Schedules
            {location && (
              <span className={styles.locationSubtitle}>{location.name}</span>
            )}
          </h2>
          <IconButton
            edge='end'
            color='inherit'
            onClick={onClose}
            className={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className={styles.modalContent}>
        <div className={styles.calendarContainer}>
          {loading ? (
            <div className={styles.placeholder}>
              <h3>Loading...</h3>
              <p>Fetching location and technician data...</p>
            </div>
          ) : (
            <div className={styles.contentLayout}>
              {/* Technician Management Panel */}
              <TechnicianPanel
                techniciansWithScheduling={techniciansWithScheduling}
                visibleTechnicianIds={visibleTechnicianIds}
                location={location}
                expandedScheduleEditors={expandedScheduleEditors}
                editingSchedules={editingSchedules}
                draftSchedulePatterns={draftSchedulePatterns}
                onToggleTechnicianVisibility={toggleTechnicianVisibility}
                onToggleScheduleEditor={toggleScheduleEditor}
                onStartEditingSchedule={startEditingSchedule}
                onCancelEditingSchedule={cancelEditingSchedule}
                onToggleDayInDraft={(techId, weekNum, day) =>
                  toggleDayInDraft(techId, weekNum, day)
                }
                onAddWeekToDraft={addWeekToDraft}
                onRemoveWeekFromDraft={(techId, weekNum) =>
                  removeWeekFromDraft(techId, weekNum)
                }
                onUpdateEditingData={(techId, field, value) =>
                  updateEditingData(techId, field, value)
                }
                onCheckChanges={(techId, workTimeData) =>
                  handleCheckChanges(techId, workTimeData)
                }
                checkingChanges={checkingChanges}
              />

              {/* Calendar Display */}
              <div className={styles.calendarPanel}>
                <h3 className={styles.panelTitle}>Schedule Calendar</h3>
                <div className={styles.bigCalendarWrapper}>
                  <CalendarView
                    calendarDate={calendarDate}
                    setCalendarDate={setCalendarDate}
                    technicianStatuses={technicianStatuses}
                    visibleTechnicianIds={visibleTechnicianIds}
                    draftSchedulePatterns={draftSchedulePatterns}
                    editingSchedules={editingSchedules}
                    onDayClick={handleDayClick}
                    onTechnicianDayClick={handleTechnicianDayClick}
                  />
                </div>

                {/* Calendar Legend */}
                <div className={styles.calendarLegend}>
                  <div className={styles.legendItems}>
                    <span className={styles.legendItem}>
                      <div
                        className={`${styles.techIndicator} ${styles.working}`}
                      ></div>
                      Working
                    </span>
                    <span className={styles.legendItem}>
                      <div
                        className={`${styles.techIndicator} ${styles.off}`}
                      ></div>
                      Off
                    </span>
                    <span className={styles.legendItem}>
                      <div
                        className={`${styles.techIndicator} ${styles.previewLegend} ${styles.humanTouched}`}
                      ></div>
                      Manually Set (blue ring)
                    </span>
                    <span className={styles.legendItem}>
                      <div
                        className={`${styles.techIndicator} ${styles.previewLegend} ${styles.previewMode}`}
                      >
                        <div className={styles.previewRing} />
                      </div>
                      Editing Preview
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      {/* Day Settings Editor Modal */}
      {selectedDate &&
        selectedTechnicianId &&
        (() => {
          const techData = getTechnicianDataForDate(
            selectedDate,
            selectedTechnicianId,
          )
          return techData ? (
            <DaySettingsEditor
              isOpen={dayEditorOpen}
              onClose={() => setDayEditorOpen(false)}
              selectedDate={selectedDate}
              technician={techData.technician}
              currentDaySetting={techData.currentDaySetting}
              isWorkingDay={techData.isWorkingDay}
              isHumanTouched={techData.isHumanTouched}
              isHoliday={techData.isHoliday}
              holidayName={techData.holidayName}
              onSaveChanges={handleSaveDayChanges}
            />
          ) : null
        })()}

      {/* Impact Preview Modal */}
      {impactModalData &&
        (() => {
          // Get current technician data to access their current schedule pattern
          const currentTechnician = techniciansWithScheduling.find(
            (t) => t.objectId === impactModalData.technicianObjectId,
          )
          const currentSchedulePattern =
            getActiveSchedulePattern(
              currentTechnician?.schedulePatterns || [],
            ) || undefined

          return (
            <ImpactPreviewModal
              open={impactModalOpen}
              onClose={handleImpactModalClose}
              technicianName={impactModalData.technicianName}
              effectiveDate={
                editingSchedules.get(impactModalData.technicianObjectId)
                  ?.effectiveFrom || ''
              }
              analysisResults={impactModalData.checkResult}
              currentSchedulePattern={currentSchedulePattern}
              newSchedulePattern={draftSchedulePatterns.get(
                impactModalData.technicianObjectId,
              )}
              onConfirmOriginalDate={handleConfirmOriginalDate}
              onUseSafeDate={handleUseSafeDate}
              savingPattern={savingPattern}
            />
          )
        })()}
    </Dialog>
  )
}
